// SPDX-FileCopyrightText: 2026 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Dialog, FooterButton, Loader, ProgressBar } from '@tls/treact-ui';
import { useExportStatus } from 'components/projectSelection/ExportStatusProvider';
import { useAppSelector } from 'store/hooks';

export default function ExportProjectDialog() {
  const { t } = useTranslation();
  const { isExporting, exportingProjectId, progress, phase, cancelExport } = useExportStatus();
  const projectName = useAppSelector(
    s => s.projects.projectsMetadata.find(p => p.projectId === exportingProjectId)?.projectName
  );

  const status = {
    preparing: t('project.export.preparing'),
    packing: t('project.export.packing'),
    transferring: t('project.export.transferring'),
  }[phase];

  return (
    <Dialog open={isExporting} size='medium' data-testid='project-export-dialog'>
      <Dialog.DialogHeader label={t('project.export.title')} />
      <Dialog.DialogContent>
        <LoaderContainer data-testid='project-export-dialog-content'>
          <ProgressContainer data-testid='project-export-dialog-progress'>
            {projectName && (
              <ProjectName className='font-size_l' data-testid='project-export-dialog-project-name'>
                {projectName}
              </ProjectName>
            )}
            <span role='status' aria-live='polite' aria-atomic='true' data-testid='project-export-dialog-status'>
              {status}
            </span>
            {phase === 'preparing' ? (
              <CompactLoader data-testid='project-export-dialog-loader'>
                <Loader label='' />
              </CompactLoader>
            ) : (
              <ProgressBar percentage={progress} data-testid='project-export-dialog-progress-bar' />
            )}
            <span className='font-size_xl' data-testid='project-export-dialog-progress-value'>
              {phase === 'preparing' ? ' ' : `${progress.toFixed(0)}%`}
            </span>
          </ProgressContainer>
        </LoaderContainer>
      </Dialog.DialogContent>
      <Dialog.Footer
        right={[
          <FooterButton
            key='cancel'
            label={t('cancel')}
            onClick={cancelExport}
            variant='secondary'
            data-testid='project-export-dialog-cancel-button'
          />,
        ]}
      />
    </Dialog>
  );
}

const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: auto 0;
`;

const LoaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

// The treact-ui <Loader> renders its bar inside a 2.5rem-tall row
// (the bar itself is 0.75rem, centered). The <ProgressBar> is just a
// 0.75rem element. Collapse the extra vertical space so both variants
// occupy the same layout slot when the phase changes.
const CompactLoader = styled.div`
  margin: -0.875rem 0;
`;

const ProjectName = styled.span`
  display: block;
  font-weight: 600;
  padding-bottom: 0.5rem;
`;
